import { Analytics } from '@gik/analytics';
import { useInkind } from '@gik/api/inkinds/inkind';
import Amazon from '@gik/core/assets/img/icons/amazon.svg';
import { useIsMounted } from '@gik/core/hooks/hooks/useIsMounted';
import { useInkindCan } from '@gik/core/store/permissions';
import bemBlock from '@gik/core/utils/bemBlock';
import { cashtagRegex, paypalMeUsernameRegex, venmoRegex } from '@gik/create/formSchemas';
import { usePageGoFundMeCampaign } from '@gik/inkind-page/api/GoFundMeAPI';
import { inkindPageTourSelectors } from '@gik/inkind-page/components/InkindPageTour/InkindPageTour';
import { CashAppSupportButton } from '@gik/inkind-page/components/Support/CashAppSupportButton';
import { GoFundMeSupportWidget } from '@gik/inkind-page/components/Support/GoFundMeSupportWidget';
import { PayPalSupportButton } from '@gik/inkind-page/components/Support/PayPalSupportButton';
import { VenmoSupportButton } from '@gik/inkind-page/components/Support/VenmoSupportButton';
import { useInkindStore } from '@gik/inkind-page/store/InkindStore';
import { Button } from '@gik/ui/Button';
import { ExternalLink } from '@gik/ui/ExternalLink';
import { Heading } from '@gik/ui/Heading';
import { SvgIcon } from '@gik/ui/SvgIcon/SvgIcon';
import { UI } from '@gik/ui/UIManager';
import React from 'react';

let hasShownPaypalNotification = false;

// TODO please remove inline styles (and fonticons if possible) upon migrating this to SPA
export default function OtherWaysToSupport() {
  const inkindRouteId = useInkindStore(state => state.inkindRouteId);
  const { data: inkindPage } = useInkind(inkindRouteId);
  const { data: goFundMeCampaignData } = usePageGoFundMeCampaign(inkindPage);

  const bem = bemBlock('other-ways-to-support');
  const isMounted = useIsMounted();
  const canEdit = useInkindCan('manage', inkindRouteId, inkindPage?.groupId) && isMounted();

  const supportWithAmazonWishList = () =>
    inkindPage?.amazonWishlist && inkindPage?.amazonWishlistRequest?.amazonWishlistUrl_WithRef;

  const hasPaypalValue =
    inkindPage?.payPal /* acceptsCrowdfunding */ && inkindPage?.financialContributions?.paypalEmail;
  const paypalSupported = hasPaypalValue && paypalMeUsernameRegex.test(inkindPage?.financialContributions?.paypalEmail);
  const supportWithGoFundMe = () =>
    inkindPage?.payPal /* acceptsCrowdfunding */ &&
    inkindPage?.financialContributions?.goFundMeUrl &&
    goFundMeCampaignData?.valid;

  const hasVenmoValue = inkindPage?.payPal /* acceptsCrowdfunding */ && inkindPage?.financialContributions?.venmoUrl;
  const venmoSupported = hasVenmoValue && venmoRegex.test(inkindPage?.financialContributions?.venmoUrl);

  const hasCashAppValue =
    inkindPage?.payPal /* acceptsCrowdfunding */ && inkindPage?.financialContributions?.cashAppUrl;
  const cashAppSupported = hasCashAppValue && cashtagRegex.test(inkindPage?.financialContributions?.cashAppUrl);

  React.useEffect(() => {
    if (hasPaypalValue && !paypalSupported && canEdit && !hasShownPaypalNotification) {
      hasShownPaypalNotification = true;
      UI.notify(
        <>
          <p>
            <strong>Where’s the PayPal button?</strong> PayPal has updated the way to send funds.
            <br />
            <ExternalLink href="http://help.giveinkind.com/en/articles/5644682-sending-and-receiving-funds-through-your-inkind-page-via-paypal">
              <span style={{ color: '#62edf0' }}>Learn how</span>
            </ExternalLink>{' '}
            to get the new PayPal button on your page.
          </p>
        </>,
        { autoClose: false }
      );
    }
  }, [hasPaypalValue, paypalSupported, canEdit]);

  return supportWithAmazonWishList() ||
    paypalSupported ||
    supportWithGoFundMe() ||
    venmoSupported ||
    cashAppSupported ? (
    <div className={bem()}>
      <Heading level={3} variant={'default-light'} className={bem('title')}>
        Other Ways to Support
      </Heading>

      {supportWithAmazonWishList() && (
        <>
          <Button
            className={bem('amazon-wishlist')}
            variant={'amazon'}
            type={'a'}
            externalLink
            href={inkindPage?.amazonWishlistRequest.amazonWishlistUrl_WithRef}
            block
            prepend={<SvgIcon Icon={Amazon} />}
          >
            Amazon Wishlist
          </Button>
        </>
      )}
      <div id={inkindPageTourSelectors.donations} className={bem('donations')}>
        {paypalSupported && (
          <PayPalSupportButton
            value={inkindPage?.financialContributions.paypalEmail}
            onClick={() => Analytics.fireDonateEvent('Paypal')}
          />
        )}
        {hasVenmoValue && (
          <VenmoSupportButton
            value={inkindPage?.financialContributions.venmoUrl}
            onClick={() => Analytics.fireDonateEvent('Venmo')}
          />
        )}
        {hasCashAppValue && (
          <CashAppSupportButton
            value={inkindPage?.financialContributions.cashAppUrl}
            onClick={() => Analytics.fireDonateEvent('CashApp')}
          />
        )}
        {supportWithGoFundMe() && (
          <GoFundMeSupportWidget className={bem('gofundme-embed')} slug={goFundMeCampaignData.slug} />
        )}
      </div>
    </div>
  ) : null;
}
